import { Grid, Typography } from '@mui/material';
import { Alert } from '@mui/lab';
import * as api from "api/Core";
import * as kapi from "api/Knowledge";
import { RequestIndicator, SpinnerIndicator } from 'components/CallServer';
import RequestErrorDialog from 'components/CallServer/RequestErrorDialog';
import { RequestErrorReport } from 'components/CallServer/RequestErrorReport';
import IconThumbUp from 'components/Icons/IconThumbUp';
import IconUserSpecialist from 'components/Icons/IconUserSpecialist';
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiClickable from 'components/MuiExtras/MuiClickable';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiLink from 'components/MuiExtras/MuiLink';
import MuiColor from 'components/MuiExtras/Typography/MuiColor';
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import HtmlUtils from 'lib/HtmlUtils';
import { UserKnowsAboutSpecialistGrantMethod } from 'models/ApiEnums';
import { PagedResult, TopicWhoKnowsAboutDto } from 'models/ApiModels';
import { RequestLifeCycle } from 'models/AppModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RoutePaths } from 'routes/RouteConfig';
import Session from 'services/Session';
import { GetEndorseMessageLine } from 'views/User/Components/UserEditKnowsAbout';
import UserEndorsementListDrawer from 'views/User/Dialogs/UserEndorsementListDrawer';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    data: PagedResult<TopicWhoKnowsAboutDto>;
    formCycle: RequestLifeCycle;
    lastError: any;
    page: number;
    eof: boolean;
    dialog: any;
}

interface IProps {
    topicId: string;
    pageCount: number;
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class TopicWhoKnowsAbout extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            data: null,
            lastError: null,
            formCycle: RequestLifeCycle.Loading,
            eof: false,
            page: 0,
            dialog: null
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Topic.Components.TopicWhoKnowsAbout." + r);
    }

    ///
    private loadData(page: number) {
        this.setState({ formCycle: RequestLifeCycle.Loading });

        const takeRecords = page * (this.props.pageCount || 32767);

        kapi.getTopicWhoKnowsAbout(
            this.props.topicId,
            null,
            0,
            takeRecords + 1
        )
            .then((data) => {
                this.setState({
                    data: {
                        items: data.items.slice(0, takeRecords),
                        total: data.total
                    },
                    page: page + 1,
                    eof: data.items.length <= takeRecords,
                    formCycle: RequestLifeCycle.None,
                });

            })
            .catch((error) => {
                this.setState({ data: null, formCycle: RequestLifeCycle.LoadFailed, page: 0, lastError: error });
            });
    }

    ///
    private handleEndorsement(userId: string, endorser: boolean) {
        this.setState({ formCycle: RequestLifeCycle.Posting });

        api.topicEndorsementUpdate(this.props.topicId, userId, endorser)
            .then((result) => {

                if (result) {
                    this.setState((state) => {
                        var newArr = state.data.items.slice();
                        var arrItem = newArr.find(p => p.userId == userId);
                        arrItem.currentUserEndorsed = endorser;
                        arrItem.endorsementsCount += endorser ? 1 : -1;

                        return {
                            data: {
                                items: newArr,
                                total: state.data.total
                            },
                            formCycle: RequestLifeCycle.None
                        };
                    });
                }

            })
            .catch((error) => {
                this.setState({ formCycle: RequestLifeCycle.PostFailed, page: 0, lastError: error });
            });
    }

    ///
    private handleEndorseView = (userId: string) => {
        this.setState({
            dialog: (
                <UserEndorsementListDrawer
                    userId={userId}
                    topicId={this.props.topicId}
                    onClose={() => this.setState({ dialog: null })}
                />
            )
        });
    };

    ///
    private renderList() {
        const { formCycle, data, page, eof } = this.state;

        return (
            <MuiContainer margin="dense">
                {
                    data != null &&
                    data.items.map((item, index) => {
                        const onEndorsementClick = item.userId == Session.user.id
                            ? null
                            : () => this.handleEndorsement(item.userId, !item.currentUserEndorsed);

                        return (
                            <Grid
                                key={index}
                                container
                                spacing={1}
                                justifyContent="space-between"
                                wrap="nowrap"
                            >
                                <Grid item>
                                    <MuiClickable
                                        title={this.res("ThumbLinkTitle")}
                                        onClick={onEndorsementClick}
                                    >
                                        <MuiColor variant={item.currentUserEndorsed === true ? "info" : "muted"}>
                                            <IconThumbUp />
                                        </MuiColor>
                                    </MuiClickable>
                                </Grid>

                                <Grid item xs zeroMinWidth>
                                    <Typography noWrap variant="subtitle2">
                                        <MuiColor variant="muted">
                                            <MuiLink
                                                color="inherit"
                                                onClick={() => HtmlUtils.openUrlInNewWindow(RoutePaths.UserProfileViewer.replace(":id?", item.userId))}
                                            >
                                                {item.name}
                                            </MuiLink>
                                            {
                                                item.specialistGrantMethod != UserKnowsAboutSpecialistGrantMethod.None &&
                                                <span>
                                                    <span>&nbsp;</span>
                                                    <IconUserSpecialist
                                                        active
                                                        color="success"
                                                        title={
                                                            item.grantedBy == Session.user.id
                                                                ? this.props.t("Views.User.Common.KnowsAbout.SpecialistByYou")
                                                                : item.specialistGrantMethod == UserKnowsAboutSpecialistGrantMethod.TopicRules
                                                                    ? this.props.t("Views.User.Common.KnowsAbout.SpecialistByTopicRules")
                                                                    : item.specialistGrantMethod == UserKnowsAboutSpecialistGrantMethod.TopicManager
                                                                        ? this.props.t("Views.User.Common.KnowsAbout.SpecialistByTopicManager")
                                                                        : this.props.t("Views.User.Common.KnowsAbout.SpecialistByUserManager")
                                                        }
                                                    />
                                                </span>
                                            }
                                        </MuiColor>
                                    </Typography>

                                    <Typography noWrap variant="subtitle1">
                                        <MuiColor variant="muted">
                                            <small>
                                                {
                                                    item.endorsementsCount > 0
                                                        ? (
                                                            <MuiLink
                                                                color="inherit"
                                                                title={this.res("EndorseTitle")}
                                                                onClick={item.endorsementsCount >= 1
                                                                    ? () => this.handleEndorseView(item.userId)
                                                                    : null
                                                                }
                                                            >
                                                                <GetEndorseMessageLine endorsementsCount={item.endorsementsCount} endorsed={item.currentUserEndorsed} />
                                                            </MuiLink>
                                                        )
                                                        : <GetEndorseMessageLine endorsementsCount={item.endorsementsCount} endorsed={item.currentUserEndorsed} />
                                                }
                                            </small>
                                        </MuiColor>
                                    </Typography>

                                </Grid>
                            </Grid>
                        );
                    })
                }

                {
                    data != null &&
                    data.total == 0 &&
                    <Alert severity="info"> {this.res("EmptyData")}</Alert>
                }

                {
                    formCycle == RequestLifeCycle.None && !eof &&
                    <GridEx item>
                        <MuiRegularButton
                            link
                            onClick={
                                () => { this.loadData(page); }
                            }
                        >
                            <span>{this.props.t("Common.Literals.LoadMore")}</span>
                        </MuiRegularButton>
                    </GridEx>
                }

                {
                    formCycle == RequestLifeCycle.Loading &&
                    <SpinnerIndicator />
                }

            </MuiContainer>
        );
    }

    ///
    public componentDidMount() {
        this.loadData(1);
    }

    ///
    public render() {
        const { lastError, formCycle, dialog } = this.state;

        if (formCycle == RequestLifeCycle.Loading)
            return <SpinnerIndicator />;

        if (lastError && formCycle == RequestLifeCycle.LoadFailed)
            return <RequestErrorReport error={lastError} />;

        return (
            <>
                {formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

                {dialog}

                {this.renderList()}
            </>);

    }
}

export default withSnackbar(withTranslation()(TopicWhoKnowsAbout));